/* ──────────────────────────────────────────────────────────────
   src/pages/reports/profesores/HorasProfesorPage.tsx
   Informe “Horas por profesor”
   • Encola el worker  (POST /reports/horas-profesor)
   • Hace polling      (GET  /reports/:id/status)
   • Muestra barra de progreso, link de descarga y tabla desglosada
   ────────────────────────────────────────────────────────────── */

import { useState } from 'react';
import dayjs from 'dayjs';
import {
  Stack,
  Button,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useHorasProfesorReport } from '../../../hooks/useReportJob';
import * as XLSX from 'xlsx';

function formatHoursToHM(value: number): string {
  const hours = Number(value) || 0;
  return hours.toLocaleString('en-US', {
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export default function HorasProfesorPage() {
  const { t } = useTranslation<'translation'>();
  /* rango inicial → mes corriente */
  const [desde, setDesde] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [hasta, setHasta] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));

  /* job + polling */
  const [jobId, setJobId] = useState<string>();
  const { run, useStatus } = useHorasProfesorReport();
  const { data: status } = useStatus(jobId);

  /* búsqueda local */
  const [search, setSearch] = useState('');

  const pendiente = !!status && !status.done && !status.failed;
  const rows = status?.result?.rows ?? [];

  const q = search.toLowerCase();
  const filtered = rows.filter((r) => r.profesor.toLowerCase().includes(q));

  /* dispara el informe */
  const handleGenerate = async () => {
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
  };

  /* maneja la descarga del archivo */
  const handleDownload = async () => {
    if (!rows.length) return;

    const excelData = rows.map((r) => ({
      profesorId: r.profesorId,
      profesor: r.profesor,

      onsite_wd: r.onsite_wd,
      onsite_we: r.onsite_we,
      onsite_hol: r.onsite_hol,

      online_wd: r.online_wd,
      online_we: r.online_we,
      online_hol: r.online_hol,

      home_wd: r.home_wd,
      home_we: r.home_we,
      home_hol: r.home_hol,

      cancel_lt2h: r.cancel_lt2h,
      exam_hours: r.exam_hours,
      total_hours: r.total_hours,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData, {
      header: [
        'profesorId',
        'profesor',
        'onsite_wd',
        'onsite_we',
        'onsite_hol',
        'online_wd',
        'online_we',
        'online_hol',
        'home_wd',
        'home_we',
        'home_hol',
        'cancel_lt2h',
        'exam_hours',
        'total_hours',
      ],
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Horas');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/octet-stream',
    });

    // 🔥 Generador de string tipo mn6cfr2i
    const randomString = () => Math.random().toString(36).substring(2, 10);

    const filename = `horas-profesor-prof-${randomString()}-${randomString()}.xlsx`;

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  /* ═════════════════════════ UI ═════════════════════════ */
  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1">
        {t('horasProfesor.titulo')}
      </Typography>

      {/* ───── barra superior ───── */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          type="date"
          label={t('horasProfesor.filtros.desde')}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          label={t('horasProfesor.filtros.hasta')}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <Button variant="contained" onClick={handleGenerate}>
          {t('horasProfesor.filtros.generar')}
        </Button>

        {rows.length > 0 && (
          <TextField
            placeholder={t<string>('horasProfesor.filtros.buscar')}
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ ml: 'auto', maxWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Stack>

      {/* ───── barra de progreso ───── */}
      {pendiente && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status!.progress} />
          <Typography variant="body2">
            {t('horasProfesor.estado.generando', {
              progress: status!.progress,
            })}
          </Typography>
        </Stack>
      )}

      {/* ───── link de descarga / error ───── */}
      {status?.done && status.result && (
        <Alert severity="success">
          {t('horasProfesor.estado.exito')}{' '}
          <Button
            variant="text"
            size="small"
            onClick={handleDownload}
            sx={{ textTransform: 'none', textDecoration: 'underline', p: 0, minWidth: 'auto' }}
          >
            {t('horasProfesor.estado.descargar')}
          </Button>{' '}
          {t('horasProfesor.estado.filas', { count: status.result.registros })}
        </Alert>
      )}
      {status?.failed && <Alert severity="error">{t('horasProfesor.estado.error')}</Alert>}

      {/* ───── tabla final ───── */}
      {status?.done && (
        <Paper sx={{ overflowX: 'auto' }}>
          {rows.length === 0 ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
            </Stack>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>{t('horasProfesor.tabla.profesor')}</b>
                  </TableCell>
                  {/* On-site */}
                  <TableCell align="right">
                    <b>{t('horasProfesor.tabla.onsiteWD')}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t('horasProfesor.tabla.onsiteWE')}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t('horasProfesor.tabla.onsiteHol')}</b>
                  </TableCell>
                  {/* Online */}
                  <TableCell align="right">
                    <b>{t('horasProfesor.tabla.onlineWD')}</b>
                  </TableCell>{' '}
                  {/* NUEVO */}
                  <TableCell align="right">
                    <b>{t('horasProfesor.tabla.onlineWE')}</b>
                  </TableCell>{' '}
                  {/* NUEVO */}
                  <TableCell align="right">
                    <b>{t('horasProfesor.tabla.onlineHol')}</b>
                  </TableCell>
                  {/* Home */}
                  <TableCell align="right">
                    <b>{t('horasProfesor.tabla.homeWD')}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t('horasProfesor.tabla.homeWE')}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t('horasProfesor.tabla.homeHol')}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t('horasProfesor.tabla.cancelLt2h')}</b>
                  </TableCell>
                  {/* Examen + Total */}
                  <TableCell align="right">
                    <b>{t('horasProfesor.tabla.exam')}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{t('horasProfesor.tabla.total')}</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.profesor}>
                    <TableCell>{r.profesor}</TableCell>
                    {/* On-site */}
                    <TableCell align="right">{formatHoursToHM(r.onsite_wd)}</TableCell>
                    <TableCell align="right">{formatHoursToHM(r.onsite_we)}</TableCell>
                    <TableCell align="right">{formatHoursToHM(r.onsite_hol)}</TableCell>
                    {/* Online */}
                    <TableCell align="right">{formatHoursToHM(r.online_wd)}</TableCell>{' '}
                    {/* NUEVO */}
                    <TableCell align="right">{formatHoursToHM(r.online_we)}</TableCell>{' '}
                    {/* NUEVO */}
                    <TableCell align="right">{formatHoursToHM(r.online_hol)}</TableCell>
                    {/* Home */}
                    <TableCell align="right">{formatHoursToHM(r.home_wd)}</TableCell>
                    <TableCell align="right">{formatHoursToHM(r.home_we)}</TableCell>
                    <TableCell align="right">{formatHoursToHM(r.home_hol)}</TableCell>
                    <TableCell align="right">{formatHoursToHM(r.cancel_lt2h)}</TableCell>
                    {/* Examen + Total */}
                    <TableCell align="right">{formatHoursToHM(r.exam_hours)}</TableCell>
                    <TableCell align="right">
                      <b>{formatHoursToHM(r.total_hours)}</b>
                    </TableCell>
                  </TableRow>
                ))}

                {!filtered.length && (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      {t('horasProfesor.tabla.vacio')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
    </Stack>
  );
}
